import { bind } from 'bind-event-listener';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { SelectionContext } from '@atlaskit/editor-common/types/selection';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type Payload } from '@atlaskit/rovo-triggers/types';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	failRemixCreateExperience,
	startRemixCreateExperience,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import { useIsAnyRemixing } from '@atlassian/malleable-ui-components/state/machine';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import {
	expVal,
	isExperimentEnabled,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { useContentType } from '@confluence/page-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { resolveRemixDynamicUiContentType, type DynamicUiSource } from './common-types.ts';
import { prepareRemixFragmentNodes } from './filterRemixFragmentNodes.ts';
import {
	deriveLegacyRendererRemixPhase,
	deriveMachineRendererRemixPhase,
} from './rendererRemixLifecycle.ts';
import { preloadRemixWatcherHost } from './RemixWatcherMount.tsx';
import { RendererRemixHighlightStyles } from './RendererRemixHighlightStyles.tsx';
import { useIsRemixAvailable } from './useIsRemixAvailable.ts';
import { useRemixPreviewFlow } from './useRemixPreviewFlow.ts';
import {
	type RendererRemixCommandHandlers,
	useRendererRemixCommands,
} from './useRendererRemixCommands.ts';
import { useRemixGenerationAnalytics } from './useRemixGenerationAnalytics.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import { useRendererRemixHighlight } from './useRendererRemixHighlight.ts';
import {
	type RendererRemixMachineLifecycle,
	useRendererRemixMachineLifecycle,
} from './useRendererRemixMachineLifecycle.ts';
import {
	type RendererRemixParityDiagnostics,
	useRendererRemixParityDiagnostics,
} from './useRendererRemixParityDiagnostics.ts';
import {
	useShouldHideAttachmentRemixOptions,
	useShouldHideDiagramRemixOption,
} from './useShouldHideDiagramRemixOption.ts';

type RendererRemixTreatment = Readonly<{
	machineLifecycle: RendererRemixMachineLifecycle;
	parityDiagnostics: RendererRemixParityDiagnostics;
	isAnyRemixing: boolean;
}>;

const constructDynamicUiSource = (
	adfDocument: DocNode,
	selectionContext: SelectionContext | null,
	contentType: DynamicUiSource['contentType'],
): DynamicUiSource | undefined => {
	const fragmentAdf = prepareRemixFragmentNodes(selectionContext?.selectionFragment ?? null);

	return {
		contentType,
		startLocalId: selectionContext?.localIds?.[0] ?? '',
		endLocalId: selectionContext?.localIds?.[selectionContext.localIds.length - 1] ?? '',
		startIndex: selectionContext?.startIndex ?? 0,
		endIndex: selectionContext?.endIndex ?? -1,
		// When the entire page is selected (e.g. cmd+a), selectionContext is null and
		// selectionFragment is not populated. Fall back to the full document content
		// so that Remix still receives the source content it needs.

		fragmentAdf: fragmentAdf ?? (adfDocument.content as any),
		fullAdf: adfDocument,
	};
};

const RENDERER_SELECTION_CONSUME_ONCE_KEY = 'renderer-selection-remix';
const publishRendererRemixHandoffFailed = () => {
	// `rovo-remix-handoff-failed` is not in the merged PubSub Payload union yet,
	// so TypeScript will not accept it as a `publish()` argument without a cast.
	publish(Topics.ROVO_REMIX, {
		type: 'rovo-remix-handoff-failed',
		source: RENDERER_SELECTION_CONSUME_ONCE_KEY,
	} as unknown as Payload);
};

const isRendererSelectionSubmit = (payload: Payload): boolean =>
	payload.type === 'submit-rovo-remix' &&
	payload.data.remixConsumer === 'renderer' &&
	payload.data.remixPreset === 'selection';

type RendererRemixStartResult =
	| { status: 'started' }
	| {
			status: 'rejected';
			reason: 'admission-rejected' | 'send-failed';
	  };

const ListenForSelectedContentRemixSubmit = ({
	currentContentId,
	hasGenerationStarted,
	onAcceptSubmit,
	onDisarm,
	originContentIdRef,
}: {
	currentContentId: string | undefined;
	hasGenerationStarted: boolean;
	onAcceptSubmit: (
		params: RemixOptionSelectionParameters & { conversationId?: string },
	) => Promise<RendererRemixStartResult>;
	onDisarm: () => void;
	originContentIdRef: React.MutableRefObject<string | undefined>;
}) => {
	const currentContentIdRef = useRef(currentContentId);
	currentContentIdRef.current = currentContentId;
	const hasGenerationStartedRef = useRef(hasGenerationStarted);
	hasGenerationStartedRef.current = hasGenerationStarted;
	const acceptedSubmitRef = useRef(false);
	const onAcceptSubmitRef = useRef(onAcceptSubmit);
	onAcceptSubmitRef.current = onAcceptSubmit;
	const onDisarmRef = useRef(onDisarm);
	onDisarmRef.current = onDisarm;

	const disarmIfIdle = useCallback(() => {
		if (hasGenerationStartedRef.current || acceptedSubmitRef.current) {
			return;
		}
		onDisarmRef.current();
	}, []);

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: RENDERER_SELECTION_CONSUME_ONCE_KEY },
		(payload) => {
			if (!isRendererSelectionSubmit(payload) || payload.type !== 'submit-rovo-remix') {
				return;
			}

			const originContentId = originContentIdRef.current;
			if (
				originContentId &&
				currentContentIdRef.current &&
				originContentId !== currentContentIdRef.current
			) {
				return;
			}

			if (acceptedSubmitRef.current || hasGenerationStartedRef.current) {
				return;
			}

			acceptedSubmitRef.current = true;
			void onAcceptSubmitRef
				.current({
					type: payload.data.remixOption,
					subType: payload.data.remixSubType,
					userPrompt: payload.data.userPrompt,
					conversationId: payload.data.conversationId,
				})
				.then((result) => {
					if (result.status === 'rejected') {
						acceptedSubmitRef.current = false;
					}
				})
				.catch(() => {
					// Keep the listener retryable if an unexpected callback failure escapes.
					acceptedSubmitRef.current = false;
				});
		},
	);

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'chat-close') {
			disarmIfIdle();
			return;
		}

		if (payload.type === 'chat-new' && payload.data.resetActiveMenu) {
			disarmIfIdle();
			return;
		}

		if (payload.type !== 'open-rovo-remix-sidebar') {
			return;
		}

		if (payload.data.remixConsumer === 'renderer' && payload.data.remixPreset === 'selection') {
			return;
		}

		disarmIfIdle();
	});

	return null;
};

/**
 * Runs the Remix clean-up when the user closes the Rovo panel before choosing a Remix
 * option.
 *
 * Closing the Rovo panel sends no message that Confluence can listen for, so the only
 * way to notice it is to watch whether the panel is currently showing.
 *
 * The same click that arms the Remix flow also asks the panel to open, so there is a
 * short moment where the panel is not showing yet. Treating that moment as a close would
 * cancel the flow the instant it started, so a close is only acted on once the panel has
 * been seen showing at least once.
 */
const CancelRemixWhenRovoPanelCloses = ({
	onRovoPanelClosed,
}: {
	onRovoPanelClosed: () => void;
}) => {
	const { isConversationAssistantSidebarActive, mode } = useToggleConversationAssistant();
	const isConversationAssistantOpen = isConversationAssistantSidebarActive || mode !== undefined;
	const hasSeenRovoPanelOpen = useRef(false);
	const onRovoPanelClosedRef = useRef(onRovoPanelClosed);
	onRovoPanelClosedRef.current = onRovoPanelClosed;

	useEffect(() => {
		if (isConversationAssistantOpen) {
			hasSeenRovoPanelOpen.current = true;
			return;
		}

		if (!hasSeenRovoPanelOpen.current) {
			return;
		}

		onRovoPanelClosedRef.current();
	}, [isConversationAssistantOpen]);

	return null;
};

type RendererRemixCreatorProps = {
	adfDocument: DocNode;
	selectionContext: SelectionContext | null;
	interactionSource?: InteractionSource;
	onClose: (options?: {
		preservePreviewSession?: boolean;
		preserveCommandSession?: boolean;
		preserveThinking?: boolean;
	}) => void;
	onFinalizeCommandSession: () => void;
	onStartThinking: () => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
};

type RendererAbortOrigin = 'local-stop' | 'navigation' | 'preview-dismissed' | 'send-rejected';

const RendererRemixCommandEffects = ({
	isAdmittedRemixId,
	onReady,
	onErrored,
	onAborted,
	onGenerationEnded,
}: {
	isAdmittedRemixId: RendererRemixMachineLifecycle['isAdmittedRemixId'];
	onReady: RendererRemixCommandHandlers['onReady'];
	onErrored: RendererRemixCommandHandlers['onErrored'];
	onAborted: RendererRemixCommandHandlers['onAborted'];
	onGenerationEnded: RendererRemixCommandHandlers['onGenerationEnded'];
}) => {
	useRendererRemixCommands(isAdmittedRemixId, {
		onReady,
		onErrored,
		onAborted,
		onGenerationEnded,
	});
	return null;
};

type RendererRemixModalProps = RendererRemixCreatorProps & {
	hiddenOptions: readonly RemixHiddenOptionKind[];
	isEphemeralViewVisible: boolean;
	isRendererOverlayOpen: boolean;
	currentContentId: string | undefined;
	anchorRect?: DOMRect;
	anchorElement?: HTMLElement;
	isThinking: boolean;
	/** Present only for the state-machine treatment. */
	treatment?: RendererRemixTreatment;
};

const RendererRemixModal = ({
	adfDocument,
	selectionContext,
	interactionSource,
	onClose,
	onFinalizeCommandSession,
	onStartThinking,
	onStopThinking,
	hiddenOptions,
	isEphemeralViewVisible,
	isRendererOverlayOpen,
	currentContentId,
	anchorRect,
	anchorElement,
	isThinking,
	treatment,
}: RendererRemixModalProps) => {
	const isMachineReadTreatment = treatment !== undefined;
	const machineLifecycle = treatment?.machineLifecycle;
	const parityDiagnostics = treatment?.parityDiagnostics;
	const [currentContentType] = useContentType();
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const experienceTracker = useAIMateExperienceTracker();
	const {
		trackGenerationFailed,
		trackGenerationStarted,
		trackGenerationStopped,
		trackGenerationSucceeded,
		trackRemixSubmitted,
	} = useRemixGenerationAnalytics();
	const unavailableSpaceOptions = useShouldHideDiagramRemixOption();
	const mergedHiddenOptions =
		unavailableSpaceOptions.length > 0
			? [...hiddenOptions, ...unavailableSpaceOptions]
			: hiddenOptions;
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const selectedSubTypeRef = useRef<string | undefined>(undefined);
	const conversationIdRef = useRef<string | undefined>(undefined);
	const remixOriginContentIdRef = useRef<string | undefined>(undefined);
	const abortOriginRef = useRef<RendererAbortOrigin>();
	const [isLegacyActionReady, setIsLegacyActionReady] = useState(false);
	const [positionedAnchorRect, setPositionedAnchorRect] = useState(anchorRect);
	const [popupRepositionKey, setPopupRepositionKey] = useState(0);
	const restartExperiencesAfterRejectedStart = useCallback(
		(rejectionReason: string) => {
			const restartAttributes = {
				entryPoint: 'renderer',
				remixSource: 'rejectedRemixStart',
				product: 'confluence',
				rejectionReason,
				...(interactionSource !== undefined ? { originalRemixSource: interactionSource } : {}),
			};
			hasCommittedRemixCreateTerminal.current = false;
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: restartAttributes,
			});
			startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
		},
		[experienceTracker, interactionSource],
	);

	useEffect(() => {
		if (remixOriginContentIdRef.current === undefined) {
			remixOriginContentIdRef.current = currentContentId;
		}
	}, [currentContentId]);

	useEffect(() => {
		setPositionedAnchorRect(anchorRect);
	}, [anchorRect]);

	useEffect(() => {
		if (!anchorElement) {
			return;
		}

		let animationFrameId: number | undefined;
		const updateAnchorPosition = () => {
			if (animationFrameId !== undefined) {
				window.cancelAnimationFrame(animationFrameId);
			}
			animationFrameId = window.requestAnimationFrame(() => {
				// Keep the last known rect: the anchor can detach while the picker is opening.
				if (!anchorElement.isConnected) {
					return;
				}
				setPositionedAnchorRect(anchorElement.getBoundingClientRect());
				setPopupRepositionKey((key) => key + 1);
			});
		};

		// `anchorRect` positions the initial render, so only follow on scroll/resize.
		const unbindScroll = bind(window, {
			type: 'scroll',
			listener: updateAnchorPosition,
			options: { capture: true, passive: true },
		});
		const unbindResize = bind(window, {
			type: 'resize',
			listener: updateAnchorPosition,
		});

		return () => {
			unbindScroll();
			unbindResize();
			if (animationFrameId !== undefined) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, [anchorElement]);

	const { dynamicUiType } = useStagingAreaState();
	const { openConversationAssistant } = useToggleConversationAssistant();
	const onPreviewOpened = useCallback(
		() => machineLifecycle?.onPreviewOpened(),
		[machineLifecycle],
	);
	const onPreviewDismissed = useCallback(
		() => machineLifecycle?.onPreviewDismissed() ?? false,
		[machineLifecycle],
	);
	const stopMachineThinking = useCallback(
		() => machineLifecycle?.onStopThinking() ?? false,
		[machineLifecycle],
	);
	const onRovoAborted = useCallback(() => machineLifecycle?.onRovoAborted(), [machineLifecycle]);
	const onErrorDismissed = useCallback(
		() => machineLifecycle?.onErrorDismissed(),
		[machineLifecycle],
	);
	const onSendRejected = useCallback(
		() => machineLifecycle?.onSendRejected() ?? false,
		[machineLifecycle],
	);
	const onNavigation = useCallback(
		() => machineLifecycle?.onNavigation() ?? false,
		[machineLifecycle],
	);

	const dismissRendererRemixUiAfterNavigation = useCallback(() => {
		const reason = 'User navigated away before renderer remix preview opened';
		isRemixCancelled.current = true;
		if (isMachineReadTreatment) {
			abortOriginRef.current = 'navigation';
			if (onNavigation()) {
				return;
			}
		}
		onStopThinking();
		onClose();
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason,
			attributes: { reason },
		});
		abortRemixCreateExperience(experienceTracker, reason);
		void remixGeneratePreviewExperience.abort({
			metadata: { reason },
		});
		if (!isMachineReadTreatment) {
			onNavigation();
		}
	}, [experienceTracker, isMachineReadTreatment, onClose, onNavigation, onStopThinking]);

	const shouldOpenPreview = useCallback(() => {
		if (isRemixCancelled.current && expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false)) {
			return false;
		}

		if (!fg('cc-maui-phase-3')) {
			return true;
		}

		const originContentId = remixOriginContentIdRef.current;

		return !originContentId || !currentContentId || originContentId === currentContentId;
	}, [currentContentId]);

	// Collapse the highlight overlay after the preview opens.
	const onAfterOpenPreview = useCallback(() => {
		onClose({ preservePreviewSession: true });
	}, [onClose]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		onActionReady: handlePreviewActionReady,
		resetCompletion,
	} = useRemixPreviewFlow({
		entryPoint: 'renderer',
		experienceTracker,
		dynamicUiType,
		selectedSubTypeRef,
		openAIFCStagingModalRef,
		isRemixCancelledRef: isRemixCancelled,
		conversationIdRef,
		shouldOpenPreview,
		onAfterOpenPreview,
	});

	const onActionReady = useCallback(() => {
		if (!isRemixCancelled.current) {
			trackGenerationSucceeded({
				conversationChannelId: conversationIdRef.current,
			});
		}
		handlePreviewActionReady();
	}, [handlePreviewActionReady, trackGenerationSucceeded]);
	const onLegacyActionReady = useCallback(() => {
		setIsLegacyActionReady(true);
		if (!isMachineReadTreatment) {
			onActionReady();
		}
	}, [isMachineReadTreatment, onActionReady]);
	const handlePreviewDismissed = useCallback(() => {
		if (isMachineReadTreatment) {
			abortOriginRef.current = 'preview-dismissed';
		}
		onPreviewDismissed();
	}, [isMachineReadTreatment, onPreviewDismissed]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		isRemixCancelled: wasRemixExplicitlyCancelled,
		remixStoreError,
		remixConversationId,
		openAIFCStagingModal,
		closeAIFCStagingModal,
		resetRemixConversationId,
	} = useRemixToAIFC({
		onActionReady: onLegacyActionReady,
		onPreviewOpened,
		onPreviewDismissed: handlePreviewDismissed,
		trackRemixConversationById: true,
	});
	openAIFCStagingModalRef.current = openAIFCStagingModal;
	const legacyRendererPhase = deriveLegacyRendererRemixPhase({
		isThinking,
		isReady: isLegacyActionReady,
		isPreviewVisible: isEphemeralViewVisible,
		isError: isRemixError,
		isAborted: isRemixAborted,
		isCancelled: wasRemixExplicitlyCancelled,
	});
	const machineRendererPhase = deriveMachineRendererRemixPhase(machineLifecycle?.phase);
	const rendererPhase = isMachineReadTreatment ? machineRendererPhase : legacyRendererPhase;
	useEffect(() => {
		if (!parityDiagnostics) {
			return;
		}
		return parityDiagnostics.recordObservation({
			enabled: isMachineReadTreatment,
			remixId: machineLifecycle?.remixId,
			legacyPhase: legacyRendererPhase,
			machinePhase: machineRendererPhase,
		});
	}, [
		isMachineReadTreatment,
		legacyRendererPhase,
		machineLifecycle?.remixId,
		machineRendererPhase,
		parityDiagnostics,
	]);
	// Preserve the legacy progress contract in control. Legacy `isThinking` remains true
	// through the normal ready interval, but unlike the normalized phase it does not revive
	// progress from stale completion state after a stopped flow reopens the picker.
	const isRendererRemixInProgress = isMachineReadTreatment
		? rendererPhase === 'thinking' || rendererPhase === 'ready'
		: isThinking;
	const hasGenerationError = rendererPhase === 'error';
	const wasGenerationAborted = rendererPhase === 'aborted';

	const handleCommandReady = useCallback(
		(command: Parameters<RendererRemixCommandHandlers['onReady']>[0]) => {
			abortOriginRef.current = undefined;
			handlePreviewActionReady();
			if (!isRemixCancelled.current) {
				trackGenerationSucceeded({ conversationChannelId: command.conversationId });
			}
		},
		[handlePreviewActionReady, trackGenerationSucceeded],
	);
	const handleCommandErrored = useCallback(
		(command: Parameters<RendererRemixCommandHandlers['onErrored']>[0]) => {
			const commandStoreError =
				remixConversationId === command.conversationId ? remixStoreError : null;
			const errorMessage = commandStoreError?.content ?? 'Rovo agent error during remix';
			if (isEphemeralViewVisible) {
				closeAIFCStagingModal();
			}
			onClose({ preserveCommandSession: true, preserveThinking: true });
			openConversationAssistant('sidebar');
			resetRemixConversationId();
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: commandStoreError?.errorType,
					traceId: commandStoreError?.traceId,
					errorStatusCode: commandStoreError?.status_code,
				},
			});
			void remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: commandStoreError?.errorType,
					traceId: commandStoreError?.traceId,
				},
			});
			trackGenerationFailed({ conversationChannelId: command.conversationId });
		},
		[
			closeAIFCStagingModal,
			experienceTracker,
			isEphemeralViewVisible,
			onClose,
			openConversationAssistant,
			remixConversationId,
			remixStoreError,
			resetRemixConversationId,
			trackGenerationFailed,
		],
	);
	const handleCommandAborted = useCallback(
		(command: Parameters<RendererRemixCommandHandlers['onAborted']>[0]) => {
			const abortOrigin = abortOriginRef.current;
			resetCompletion();

			if (abortOrigin === 'preview-dismissed' || abortOrigin === 'send-rejected') {
				return;
			}

			if (abortOrigin === 'navigation') {
				const reason = 'User navigated away before renderer remix preview opened';
				onClose({ preserveCommandSession: true, preserveThinking: true });
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason,
					attributes: { reason },
				});
				abortRemixCreateExperience(experienceTracker, reason);
				void remixGeneratePreviewExperience.abort({ metadata: { reason } });
				return;
			}

			const reason =
				abortOrigin === 'local-stop'
					? 'User cancelled remix thinking'
					: 'User stopped from Rovo side chat';
			if (abortOrigin !== 'local-stop') {
				// The modal store can lag the terminal conversation snapshot, so closing is
				// intentionally idempotent even when render-time visibility is still false.
				if (expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false)) {
					isRemixCancelled.current = true;
					closeAIFCStagingModal();
				} else if (isEphemeralViewVisible) {
					closeAIFCStagingModal();
				}
			}

			const closesRendererUi =
				abortOrigin !== 'local-stop' || fg('platform_editor_ai_remix_close_modal_on_stop');
			if (closesRendererUi) {
				onClose({ preserveCommandSession: true, preserveThinking: true });
			}

			try {
				if (abortOrigin === 'local-stop') {
					experienceTracker.abort({
						name: ExperienceName.REMIX_START,
						reason,
						attributes: { reason },
					});
					void remixGeneratePreviewExperience.abort({ metadata: { reason } });
				}
				abortRemixCreateExperience(experienceTracker, reason);
				trackGenerationStopped({ conversationChannelId: command.conversationId });
			} finally {
				if (!closesRendererUi) {
					const restartAttributes = {
						entryPoint: 'renderer',
						remixSource: 'cancelledRemixThinking',
						originalRemixSource: 'selectionToolbar',
						product: 'confluence',
					};
					experienceTracker.start({
						name: ExperienceName.REMIX_START,
						id: ExperienceName.REMIX_START,
						attributes: restartAttributes,
					});
					startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
				}
			}
		},
		[
			closeAIFCStagingModal,
			experienceTracker,
			isEphemeralViewVisible,
			onClose,
			resetCompletion,
			trackGenerationStopped,
		],
	);
	const handleCommandGenerationEnded = useCallback(
		(command: Parameters<RendererRemixCommandHandlers['onGenerationEnded']>[0]) => {
			const abortOrigin = abortOriginRef.current;
			const closesTreatmentSession =
				command.reason === 'error' ||
				(command.reason === 'aborted' &&
					abortOrigin !== 'preview-dismissed' &&
					abortOrigin !== 'send-rejected' &&
					(abortOrigin !== 'local-stop' || fg('platform_editor_ai_remix_close_modal_on_stop')));
			if (abortOrigin !== 'preview-dismissed') {
				onStopThinking({
					reopenModal:
						command.reason === 'ready' ||
						abortOrigin === 'send-rejected' ||
						(abortOrigin === 'local-stop' && !fg('platform_editor_ai_remix_close_modal_on_stop')),
				});
			}
			if (command.reason === 'error') {
				onErrorDismissed();
			} else if (command.reason === 'aborted' && abortOrigin === undefined) {
				onRovoAborted();
			}
			if (closesTreatmentSession) {
				onFinalizeCommandSession();
			}
			abortOriginRef.current = undefined;
		},
		[onErrorDismissed, onFinalizeCommandSession, onRovoAborted, onStopThinking],
	);

	useEffect(() => {
		if (!fg('cc-maui-phase-3')) {
			return;
		}

		const originContentId = remixOriginContentIdRef.current;

		if (!originContentId || !currentContentId || originContentId === currentContentId) {
			return;
		}

		dismissRendererRemixUiAfterNavigation();
	}, [currentContentId, dismissRendererRemixUiAfterNavigation]);

	// Abort experience on unmount (overlay closed without selecting an option).
	// Uses a ref so the cleanup always calls the latest tracker reference.
	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasCommittedRemixCreateTerminal.current,
			);
		};
	}, []);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean) => {
			if (active) {
				isRemixCancelled.current = false;
				abortOriginRef.current = undefined;
				// Clear stale completion state from any previous flow.
				resetCompletion();
				setIsLegacyActionReady(false);
				onStartThinking();
			} else {
				isRemixCancelled.current = true;
				// A stopped flow can already have reached the completed-progress interval.
				// Clear it before reopening so control does not render a stale ready picker.
				resetCompletion();
				setIsLegacyActionReady(false);
				if (isMachineReadTreatment) {
					abortOriginRef.current = 'local-stop';
					remixOriginContentIdRef.current = undefined;
					let didApplyTerminalEvent = false;
					try {
						stopRemixing();
						parityDiagnostics?.recordLegacyTerminal(machineLifecycle?.remixId, 'aborted');
					} finally {
						didApplyTerminalEvent = stopMachineThinking();
					}
					if (!didApplyTerminalEvent) {
						const reason = 'User cancelled remix thinking';
						if (fg('platform_editor_ai_remix_close_modal_on_stop')) {
							onClose();
						} else {
							onStopThinking({ reopenModal: true });
						}
						try {
							experienceTracker.abort({
								name: ExperienceName.REMIX_START,
								reason,
								attributes: { reason },
							});
							abortRemixCreateExperience(experienceTracker, reason);
							void remixGeneratePreviewExperience.abort({ metadata: { reason } });
							trackGenerationStopped({
								conversationChannelId: conversationIdRef.current,
							});
						} finally {
							if (!fg('platform_editor_ai_remix_close_modal_on_stop')) {
								const restartAttributes = {
									entryPoint: 'renderer',
									remixSource: 'cancelledRemixThinking',
									originalRemixSource: 'selectionToolbar',
									product: 'confluence',
								};
								experienceTracker.start({
									name: ExperienceName.REMIX_START,
									id: ExperienceName.REMIX_START,
									attributes: restartAttributes,
								});
								startRemixCreateExperience({
									experienceTracker,
									attributes: restartAttributes,
								});
							}
						}
					}
					return;
				}
				// Abort the current experience before stopping
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
				void remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				trackGenerationStopped();
				remixOriginContentIdRef.current = undefined;
				try {
					stopRemixing();
					parityDiagnostics?.recordLegacyTerminal(machineLifecycle?.remixId, 'aborted');
				} finally {
					stopMachineThinking();
				}
				if (fg('platform_editor_ai_remix_close_modal_on_stop')) {
					onClose();
				} else {
					// Start a new experience for the reopened overlay
					const restartAttributes = {
						entryPoint: 'renderer',
						remixSource: 'cancelledRemixThinking',
						originalRemixSource: 'selectionToolbar',
						product: 'confluence',
					};
					experienceTracker.start({
						name: ExperienceName.REMIX_START,
						id: ExperienceName.REMIX_START,
						attributes: restartAttributes,
					});
					startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
					onStopThinking({ reopenModal: true });
				}
			}
		},
		[
			onStartThinking,
			stopRemixing,
			stopMachineThinking,
			onClose,
			onStopThinking,
			experienceTracker,
			resetCompletion,
			parityDiagnostics,
			machineLifecycle?.remixId,
			trackGenerationStopped,
			isMachineReadTreatment,
		],
	);

	// Fail experience and clean up thinking UI when Rovo encounters an error
	const prevHasGenerationError = useRef(hasGenerationError);
	useEffect(() => {
		if (
			!isMachineReadTreatment &&
			!prevHasGenerationError.current &&
			hasGenerationError &&
			!isRemixCancelled.current
		) {
			const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
			experienceTracker.fail({
				name: ExperienceName.REMIX_START,
				error: new Error(errorMessage),
				attributes: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
					errorStatusCode: remixStoreError?.status_code,
				},
			});
			void remixGeneratePreviewExperience.failure({
				metadata: {
					errorType: remixStoreError?.errorType,
					traceId: remixStoreError?.traceId,
				},
			});
			trackGenerationFailed({
				conversationChannelId: conversationIdRef.current,
			});
			onStopThinking({ reopenModal: true });
		}
		prevHasGenerationError.current = hasGenerationError;
	}, [
		hasGenerationError,
		isMachineReadTreatment,
		remixStoreError,
		onStopThinking,
		experienceTracker,
		trackGenerationFailed,
	]);

	// When an error occurs during remix, close the thinking bar and ephemeral
	// preview, then open the Rovo chat sidebar so the user can see the error.
	useEffect(() => {
		if (!isMachineReadTreatment && !isRemixCancelled.current && hasGenerationError) {
			// Close the ephemeral preview (staging modal) if it is open
			if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- terminal cleanup intentionally settles each existing product store
				closeAIFCStagingModal();
			}
			// Close the thinking bar and dismiss the remix UI
			onStopThinking();
			onClose();
			// Open the Rovo chat sidebar to display the error information.
			// `openConversationAssistant` is idempotent (no-op if already
			// open), so it is safe to call on every render where
			// the selected phase is `error` — no infinite toggle loop.
			openConversationAssistant('sidebar');
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- terminal cleanup intentionally settles each existing product store
			resetRemixConversationId();
			onErrorDismissed();
		}
	}, [
		hasGenerationError,
		isMachineReadTreatment,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		openConversationAssistant,
		resetRemixConversationId,
		onErrorDismissed,
	]);

	// Dismiss the thinking bar and clear the selection highlight when the user
	// stops the remix from the Rovo side chat.
	useEffect(() => {
		if (!isMachineReadTreatment && wasGenerationAborted && !isRemixCancelled.current) {
			const reason = 'User stopped from Rovo side chat';
			if (expVal('cc_maui_polish_changes_batch_5', 'isEnabled', false)) {
				isRemixCancelled.current = true;
				resetCompletion();
				// The modal store visibility can lag behind action readiness for
				// non-streaming remix types. Closing is idempotent, so do it even
				// when the render-time visibility snapshot is still false.
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- terminal cleanup intentionally settles each existing product store
				closeAIFCStagingModal();
			} else if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- terminal cleanup intentionally settles each existing product store
				closeAIFCStagingModal();
			}
			abortRemixCreateExperience(experienceTracker, reason);
			trackGenerationStopped({
				conversationChannelId: conversationIdRef.current,
			});
			onStopThinking();
			onClose();
			onRovoAborted();
		}
	}, [
		wasGenerationAborted,
		isMachineReadTreatment,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		onStopThinking,
		onClose,
		experienceTracker,
		onRovoAborted,
		resetCompletion,
		trackGenerationStopped,
	]);

	const handleOptionSelect = useCallback(
		async ({
			type,
			subType,
			userPrompt,
			conversationId,
		}: RemixOptionSelectionParameters & {
			conversationId?: string;
		}): Promise<RendererRemixStartResult> => {
			// Only a submission from the Rovo sidebar carries a conversationId, and the Rovo
			// sidebar is only shown to the variant2 cohort. Its `userPrompt` is already the
			// complete sentence shown in the chat input, so passing that sentence as
			// `promptText` stops `sendAIFCPrompt` from writing a second copy of the same
			// sentence and joining the two together. Checking the cohort as well as the
			// conversationId guarantees that anyone outside the experiment keeps the original
			// behaviour, even if some future caller starts supplying a conversationId.
			const isRovoChatSubmission =
				conversationId !== undefined &&
				UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
			remixOriginContentIdRef.current = remixOriginContentIdRef.current ?? currentContentId;
			selectedSubTypeRef.current = subType;

			const dynamicUiSource = constructDynamicUiSource(
				adfDocument,
				selectionContext,
				resolveRemixDynamicUiContentType(currentContentType),
			);
			let authoritativeConversationId = conversationId;
			let cancellationSessionId: string | undefined;
			if (machineLifecycle) {
				const admission = machineLifecycle.startFlow({
					conversationId,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					source: interactionSource,
				});
				if (!admission.admitted) {
					if (admission.reason !== 'active-flow') {
						const admissionError = new Error(
							`Renderer Remix machine admission was rejected: ${admission.reason}`,
						);
						experienceTracker.fail({
							name: ExperienceName.REMIX_START,
							error: admissionError,
							attributes: {
								reason: admissionError.message,
								rejectionReason: admission.reason,
							},
						});
						abortRemixCreateExperience(experienceTracker, admissionError.message);
						restartExperiencesAfterRejectedStart(admission.reason);
					}
					onStopThinking({ reopenModal: true });
					return { status: 'rejected', reason: 'admission-rejected' };
				}
				authoritativeConversationId = admission.conversationId;
				cancellationSessionId = admission.cancellationSessionId;
			}

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { remixType: type, remixSubtype: subType, includesFreegenPrompt: !!userPrompt },
			});
			void remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				entryPoint: 'renderer',
				remixType: type,
				remixSubtype: subType,
				includesFreegenPrompt: !!userPrompt,
				...(type === 'infographic'
					? {
							isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
						}
					: {}),
			});
			const generationAnalyticsAttributes = {
				contentId: currentContentId,
				...(conversationId && { conversationChannelId: conversationId }),
				entryPoint: 'renderer',
				includesFreegenPrompt: !!userPrompt,
				product: 'confluence',
				remixSource: interactionSource,
				remixSubtype: subType,
				remixType: type,
			};
			trackRemixSubmitted(generationAnalyticsAttributes);
			trackGenerationStarted(generationAnalyticsAttributes);
			let conversationChannelId: string;
			try {
				conversationChannelId = await sendAIFCPrompt({
					userPrompt: isRovoChatSubmission ? undefined : userPrompt,
					promptText: isRovoChatSubmission ? userPrompt : undefined,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource,
					...(authoritativeConversationId !== undefined
						? { conversationId: authoritativeConversationId }
						: {}),
					...(cancellationSessionId !== undefined
						? { remixCancellationSessionId: cancellationSessionId }
						: {}),
				});
			} catch (error) {
				if (isMachineReadTreatment) {
					abortOriginRef.current = 'send-rejected';
				}
				const didApplyTerminalEvent = onSendRejected();
				if (isMachineReadTreatment && !didApplyTerminalEvent) {
					onStopThinking({ reopenModal: true });
				}
				if (
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
				) {
					publishRendererRemixHandoffFailed();
				}
				const sendError =
					error instanceof Error ? error : new Error('Failed to send renderer Remix prompt');
				const reason = 'Failed to send renderer Remix prompt';
				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: sendError,
					attributes: {
						reason,
						rejectionReason: 'send-failed',
					},
				});
				void remixGeneratePreviewExperience.failure({
					metadata: { reason },
				});
				trackGenerationFailed();
				failRemixCreateExperience(experienceTracker, sendError, {
					reason,
					rejectionReason: 'send-failed',
				});
				restartExperiencesAfterRejectedStart('send-failed');
				if (!isMachineReadTreatment) {
					onStopThinking({ reopenModal: true });
				}
				return { status: 'rejected', reason: 'send-failed' };
			}
			// Stash the conversationId so `useRemixPreviewFlow` can include it as
			// `conversationChannelId` on the REMIX_RENDERS_IN_MODAL start attributes
			// once `onActionReady` fires.
			conversationIdRef.current = conversationChannelId;
			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: { conversationChannelId },
			});
			hasCommittedRemixCreateTerminal.current = true;
			trackRemixCreateOptionSelected({
				experienceTracker,
				conversationId: conversationChannelId,
				remixType: type,
				remixSubtype: subType,
				includesFreegenPrompt: !!userPrompt,
			});
			return { status: 'started' };
		},
		[
			sendAIFCPrompt,
			adfDocument,
			selectionContext,
			currentContentType,
			interactionSource,
			experienceTracker,
			currentContentId,
			isMachineReadTreatment,
			machineLifecycle,
			onSendRejected,
			onStopThinking,
			restartExperiencesAfterRejectedStart,
			trackGenerationFailed,
			trackGenerationStarted,
			trackRemixSubmitted,
		],
	);

	const acceptRovoRemixSubmit = useCallback(
		(
			params: RemixOptionSelectionParameters & { conversationId?: string },
		): Promise<RendererRemixStartResult> => {
			onRemixFlowActiveChange(true);
			return handleOptionSelect(params);
		},
		[handleOptionSelect, onRemixFlowActiveChange],
	);

	const shouldShowOriginalRemixPopUpWindow =
		isRendererRemixInProgress ||
		!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');

	return (
		<>
			{machineLifecycle && (
				<RendererRemixCommandEffects
					isAdmittedRemixId={machineLifecycle.isAdmittedRemixId}
					onReady={handleCommandReady}
					onErrored={handleCommandErrored}
					onAborted={handleCommandAborted}
					onGenerationEnded={handleCommandGenerationEnded}
				/>
			)}
			{isRendererOverlayOpen &&
				UNSAFE_expValEqualsNoExposure(
					'cc-ce-maui-remix-menu-multivariant',
					'cohort',
					'variant2',
				) && (
					<ListenForSelectedContentRemixSubmit
						currentContentId={currentContentId}
						hasGenerationStarted={isRendererRemixInProgress}
						onAcceptSubmit={acceptRovoRemixSubmit}
						onDisarm={onClose}
						originContentIdRef={remixOriginContentIdRef}
					/>
				)}
			{isRendererOverlayOpen && shouldShowOriginalRemixPopUpWindow && (
				<RemixContainer
					onClose={() => onClose()}
					hiddenOptions={mergedHiddenOptions}
					onOptionSelect={handleOptionSelect}
					onRemixFlowActiveChange={onRemixFlowActiveChange}
					isEphemeralViewVisible={isEphemeralViewVisible}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					// Classic view page: center the fixed thinking bar over the rendered content area.
					// Safe here because the view page has a stable layout (unlike the editor, whose
					// reflows would make the one-shot content measurement drift).
					shouldMeasureContentBounds
					// The view floating toolbar provides a live element that is remeasured as it moves.
					// Snapshot-only renderer entries, such as the nudge, still close on external scroll.
					anchorRect={positionedAnchorRect}
					popupRepositionKey={popupRepositionKey}
					shouldCloseOnScroll={!anchorElement}
					isThinking={
						isMachineReadTreatment
							? isRendererRemixInProgress
							: isRendererRemixInProgress &&
								  UNSAFE_expValEqualsNoExposure(
										'cc-ce-maui-remix-menu-multivariant',
										'cohort',
										'variant2',
								  )
								? true
								: undefined
					}
					// The nudge icon sits at the top-right of a block; flying the popup out to the right
					// would frequently clip against the viewport edge, so it opens to the left instead.
					popupPlacement={
						interactionSource === 'remixModalFromViewPageNudge' ? 'bottom-end' : 'bottom-start'
					}
					// Match the view selection toolbar's ellipsis DropdownMenu default offset.
					popupOffset={[0, 8]}
				/>
			)}
		</>
	);
};

export type RemixDisabledReason = 'archived' | 'version-history';

type RendererRemixContextProps = {
	openRemixModal: (
		adfDocument: DocNode,
		selection: SelectionContext | null,
		interactionSource?: InteractionSource,
		anchorRect?: DOMRect,
		anchorElement?: HTMLElement,
	) => void;
	closeRemixModal: () => void;
	clearRemixHighlight: () => void;
	isRemixDisabled: boolean;
	isRemixing: boolean;
	isRemixInProgress: boolean;
	// Whether the active Remix prevents another entry point from targeting different content.
	isRemixBlockingNewTargets: boolean;
	// Treatment-only shared lifecycle activity. Unlike the local blocking policy above, this also
	// represents a Remix admitted by another surface and must override local cohort exceptions.
	isAnyRemixing: boolean;
	// Which control the Remix that is currently open was started from, so that a control which
	// decorates the page can tell whether the content being Remixed is still the content it
	// pointed at. Undefined when no Remix is open.
	remixInteractionSource: InteractionSource | undefined;
	remixDisabledReason?: RemixDisabledReason;
	registerOnModalClose: (callback: () => void) => () => void;
};
const RendererRemixContext = createContext<RendererRemixContextProps>({
	openRemixModal: () => {},
	closeRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: false,
	isRemixing: false,
	isRemixInProgress: false,
	isRemixBlockingNewTargets: false,
	isAnyRemixing: false,
	remixInteractionSource: undefined,
	registerOnModalClose: () => () => {},
});

export const useRendererRemixContext = (): RendererRemixContextProps => {
	return useContext(RendererRemixContext);
};

const RendererRemixContainerImpl = ({
	children,
	contentId,
	treatment,
}: {
	children: React.ReactNode;
	contentId?: string;
	treatment?: RendererRemixTreatment;
}) => {
	const isRemixAvailable = useIsRemixAvailable();
	// Prefetch only attachment permission. Existing space-type checks remain modal-local.
	const hiddenOptions = useShouldHideAttachmentRemixOptions({ enabled: isRemixAvailable });
	const isMachineReadTreatment = treatment !== undefined;
	const isAnyRemixing = treatment?.isAnyRemixing ?? false;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [adfDocument, setAdfDocument] = useState<DocNode | null>(null);
	const [selectionContext, setSelectionContext] = useState<SelectionContext | null>(null);
	const [isThinking, setIsThinking] = useState(false);
	const [anchorRect, setAnchorRect] = useState<DOMRect | undefined>(undefined);
	const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
	const [remixInteractionSource, setRemixInteractionSource] = useState<
		InteractionSource | undefined
	>(undefined);
	const { startHighlight, clearHighlight } = useRendererRemixHighlight();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const onModalCloseCallbacks = useRef<Set<() => void>>(new Set());

	const registerOnModalClose = useCallback((callback: () => void) => {
		onModalCloseCallbacks.current.add(callback);
		return () => {
			onModalCloseCallbacks.current.delete(callback);
		};
	}, []);
	const clearModalSession = useCallback(() => {
		setAdfDocument(null);
		setSelectionContext(null);
		setRemixInteractionSource(undefined);
		setAnchorRect(undefined);
		setAnchorElement(undefined);
	}, []);
	const prevShowSmartCreationModal = useRef(showSmartCreationModal);

	useEffect(() => {
		if (prevShowSmartCreationModal.current && !showSmartCreationModal) {
			clearHighlight();
			// Smart Creation owns the preview and can close outside this container's closeModal path.
			// Release the renderer session when that external modal-store transition completes.
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			clearModalSession();
		}
		prevShowSmartCreationModal.current = showSmartCreationModal;
	}, [showSmartCreationModal, clearHighlight, clearModalSession]);

	const openModal = useCallback(
		(
			newAdfDocument: DocNode,
			selection: SelectionContext | null,
			interactionSource?: InteractionSource,
			newAnchorRect?: DOMRect,
			newAnchorElement?: HTMLElement,
		) => {
			// Record the Remix menu experiment exposure here. This is the one point that the
			// selection toolbar and the nudge both call for every cohort, and it is where the
			// cohort decides which Remix picker the person is about to see. Recording it further
			// downstream, once each cohort's own picker had appeared, undercounted the variant2
			// group, because their picker only appears after the Rovo side panel and two lazily
			// loaded chunks have all arrived.
			expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
			expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

			if (isMachineReadTreatment) {
				// Only treatment admits a machine flow from this modal. The background-phase watcher
				// host may not have loaded yet on a fresh page, so start its fetch now for that flow.
				preloadRemixWatcherHost();
			}
			setAdfDocument(newAdfDocument);
			startHighlight();
			setSelectionContext(selection);
			setRemixInteractionSource(interactionSource);
			setAnchorRect(newAnchorRect);
			setAnchorElement(newAnchorElement);
			setIsModalOpen(true);
		},
		[isMachineReadTreatment, startHighlight],
	);

	const closeModal = useCallback(
		(options?: {
			preservePreviewSession?: boolean;
			preserveCommandSession?: boolean;
			preserveThinking?: boolean;
		}) => {
			if (!options?.preservePreviewSession) {
				clearHighlight();
			}
			onModalCloseCallbacks.current.forEach((cb) => cb());
			setIsModalOpen(false);
			if (!options?.preserveThinking) {
				setIsThinking(false);
			}
			// Treatment keeps the phase-consuming modal subtree mounted while the Smart Creation
			// preview is open, so watcher terminal phases can drive renderer UX before teardown.
			if (
				(!options?.preservePreviewSession && !options?.preserveCommandSession) ||
				!isMachineReadTreatment
			) {
				clearModalSession();
			}
		},
		[clearHighlight, clearModalSession, isMachineReadTreatment],
	);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	const rendererPhase = treatment
		? deriveMachineRendererRemixPhase(treatment.machineLifecycle.phase)
		: deriveLegacyRendererRemixPhase({
				isThinking,
				isReady: false,
				isPreviewVisible: showSmartCreationModal,
				isError: false,
				isAborted: false,
				isCancelled: false,
			});

	const isRemixInProgress = rendererPhase === 'thinking' || rendererPhase === 'ready';
	const isRemixCommitted =
		showSmartCreationModal ||
		isRemixInProgress ||
		rendererPhase === 'previewing' ||
		rendererPhase === 'submitting';
	// Variant 2 alone keeps its local picker active while the user chooses a target. Every
	// other cohort, and every committed flow, keeps the existing single-target policy.
	const context = useMemo(() => {
		const isRemixing = isModalOpen || isRemixCommitted;
		const isRemixBlockingNewTargets =
			isAnyRemixing ||
			(isRemixing &&
				(isRemixCommitted ||
					!UNSAFE_expValEqualsNoExposure(
						'cc-ce-maui-remix-menu-multivariant',
						'cohort',
						'variant2',
					)));
		return {
			openRemixModal: openModal,
			closeRemixModal: closeModal,
			clearRemixHighlight: clearHighlight,
			isRemixDisabled: false,
			// These values describe how far along a Remix is, and they deliberately differ from
			// one another:
			//
			// - `isRemixing` becomes true as soon as any Remix flow opens, which includes the time
			//   the person spends looking at the list of Remix choices before picking one.
			// - `isRemixInProgress` is true only while content is being generated or has just
			//   finished being generated.
			// - `isRemixBlockingNewTargets` is true when another entry point must not replace the
			//   currently targeted content.
			isRemixing,
			isRemixInProgress,
			isRemixBlockingNewTargets,
			isAnyRemixing,
			remixInteractionSource,
			registerOnModalClose,
		};
	}, [
		openModal,
		closeModal,
		clearHighlight,
		isModalOpen,
		isRemixCommitted,
		isRemixInProgress,
		isAnyRemixing,
		remixInteractionSource,
		registerOnModalClose,
	]);

	return (
		<RendererRemixContext.Provider value={context}>
			<RendererRemixHighlightStyles />
			{children}
			{/*
			    This is only needed by the people in the cc-ce-maui-remix-menu-multivariant
			    experiment who see the list of Remix options inside the Rovo panel. Everyone else
			    sees those options in a pop-up window that Confluence opens and closes itself, so
			    Confluence already knows when that window has gone away.

			    It is only added to the page while the person is still choosing an option and
			    nothing has been written yet, so a Remix that has already started writing is
			    never cancelled.

			    It is placed here rather than inside ConfluenceConversationAssistant just below,
			    because that part of the page is only downloaded the first time it is needed, and
			    anything inside it would start watching too late to notice the panel closing.
			*/}
			{isModalOpen &&
				!isThinking &&
				!isRemixCommitted &&
				UNSAFE_expValEqualsNoExposure(
					'cc-ce-maui-remix-menu-multivariant',
					'cohort',
					'variant2',
				) && <CancelRemixWhenRovoPanelCloses onRovoPanelClosed={closeModal} />}
			{(isModalOpen || isThinking || isMachineReadTreatment) && adfDocument && (
				<ConfluenceConversationAssistant>
					<RendererRemixModal
						adfDocument={adfDocument}
						hiddenOptions={hiddenOptions}
						selectionContext={selectionContext}
						interactionSource={remixInteractionSource}
						onClose={closeModal}
						onFinalizeCommandSession={clearModalSession}
						onStartThinking={onStartThinking}
						onStopThinking={onStopThinking}
						isEphemeralViewVisible={showSmartCreationModal}
						isRendererOverlayOpen={isModalOpen || isThinking}
						currentContentId={contentId}
						anchorRect={anchorRect}
						anchorElement={anchorElement}
						isThinking={isThinking}
						treatment={treatment}
					/>
				</ConfluenceConversationAssistant>
			)}
		</RendererRemixContext.Provider>
	);
};

const RendererRemixTreatmentHost = ({
	children,
	contentId,
}: {
	children: React.ReactNode;
	contentId?: string;
}): React.JSX.Element => {
	// CCCEMAU-3500 parity diagnostics - remove with the Remix state management refactor.
	const parityDiagnostics = useRendererRemixParityDiagnostics();
	const machineLifecycle = useRendererRemixMachineLifecycle({
		onLifecycleBoundary: parityDiagnostics.recordLifecycleBoundary,
	});
	const isAnyRemixing = useIsAnyRemixing();
	const treatment = useMemo(
		() => ({ machineLifecycle, parityDiagnostics, isAnyRemixing }),
		[machineLifecycle, parityDiagnostics, isAnyRemixing],
	);

	return (
		<RendererRemixContainerImpl contentId={contentId} treatment={treatment}>
			{children}
		</RendererRemixContainerImpl>
	);
};

const RendererRemixTreatmentDecision = ({
	children,
	contentId,
	isRemixAvailable,
	isLifecycleAdapterAvailable,
}: {
	children: React.ReactNode;
	contentId?: string;
	isRemixAvailable: boolean;
	isLifecycleAdapterAvailable: boolean;
}): React.JSX.Element => {
	// This decision is intentionally fixed for the mounted renderer surface. The regular experiment
	// evaluation is therefore the first Statsig exposure for both control and treatment, while every
	// earlier eligibility sentinel short-circuits without evaluating the surface experiment.
	const [isMachineReadTreatment] = useState(
		() =>
			isRemixAvailable &&
			isLifecycleAdapterAvailable &&
			fg('remix_state_rearchitecture_enabled') &&
			isExperimentEnabled('remix_state_rearchitecture_renderer'),
	);

	if (isMachineReadTreatment) {
		return (
			<RendererRemixTreatmentHost contentId={contentId}>{children}</RendererRemixTreatmentHost>
		);
	}

	return <RendererRemixContainerImpl contentId={contentId}>{children}</RendererRemixContainerImpl>;
};

const RendererRemixContainerOwner = ({
	children,
	contentId,
	isLifecycleAdapterAvailable,
}: {
	children: React.ReactNode;
	contentId?: string;
	isLifecycleAdapterAvailable: boolean;
}): React.JSX.Element => {
	const isRemixAvailable = useIsRemixAvailable();
	// Availability and adapter capability are both host-boundaries. If either changes, remounting
	// reevaluates eligibility before any treatment hook is mounted rather than pinning a stale control
	// decision in the existing renderer session.
	const eligibilityKey = `${isRemixAvailable}:${isLifecycleAdapterAvailable}`;

	return (
		<RendererRemixTreatmentDecision
			key={eligibilityKey}
			contentId={contentId}
			isRemixAvailable={isRemixAvailable}
			isLifecycleAdapterAvailable={isLifecycleAdapterAvailable}
		>
			{children}
		</RendererRemixTreatmentDecision>
	);
};

const makeDisabledRemixContextValue = (
	reason?: RemixDisabledReason,
): RendererRemixContextProps => ({
	openRemixModal: () => {},
	closeRemixModal: () => {},
	clearRemixHighlight: () => {},
	isRemixDisabled: true,
	isRemixing: false,
	isRemixInProgress: false,
	isRemixBlockingNewTargets: false,
	isAnyRemixing: false,
	remixInteractionSource: undefined,
	remixDisabledReason: reason,
	registerOnModalClose: () => () => {},
});

const disabledRemixContextValue = makeDisabledRemixContextValue();

export const RendererRemixContainer = ({
	children,
	contentId,
	isDisabled,
	remixDisabledReason,
	isLifecycleAdapterAvailable = false,
}: {
	children: React.ReactNode;
	contentId?: string;
	isDisabled?: boolean;
	remixDisabledReason?: RemixDisabledReason;
	/** Whether this renderer host provides the state-machine lifecycle adapter. */
	isLifecycleAdapterAvailable?: boolean;
}): React.JSX.Element => {
	if (isDisabled) {
		const contextValue = remixDisabledReason
			? makeDisabledRemixContextValue(remixDisabledReason)
			: disabledRemixContextValue;
		return (
			<RendererRemixContext.Provider value={contextValue}>{children}</RendererRemixContext.Provider>
		);
	}

	return (
		<RendererRemixContainerOwner
			contentId={contentId}
			isLifecycleAdapterAvailable={isLifecycleAdapterAvailable}
		>
			{children}
		</RendererRemixContainerOwner>
	);
};
